import { Emitter } from "@core/events/emitter";
import type { IProjectModel } from "@models/project/IProjectModel";
import type {
  BoundingBoxOrigin,
  BoundingBoxTarget,
  IBoundingBoxToolModel,
  MeasureVisual,
} from "./IBoundingBoxToolModel";

/**
 * `BoundingBoxToolModel` — port of `Models/BoundingBoxToolModel.cs` minus
 * the GameObject spawning (the web `BoundingBoxToolController` renders
 * boxes from `targets`). Boxes clear on scene change / project unload,
 * mirroring Unity's `BeginLoadScene` / `BeginUnloadProject` hooks in
 * `MeasurementTool`.
 */
export class BoundingBoxToolModel implements IBoundingBoxToolModel {
  readonly onChanged = new Emitter<ReadonlyArray<BoundingBoxTarget>>();

  private m_targets: BoundingBoxTarget[] = [];
  private readonly m_offFns: Array<() => void> = [];

  constructor(project: IProjectModel) {
    this.m_offFns.push(
      project.onActiveSceneChanged.on(() => this.clearAll()),
      project.onActiveProjectChanged.on(() => this.clearAll()),
    );
  }

  dispose(): void {
    for (const off of this.m_offFns) off();
    this.m_offFns.length = 0;
    this.m_targets = [];
    this.onChanged.clear();
  }

  get targets(): ReadonlyArray<BoundingBoxTarget> {
    return this.m_targets;
  }

  addBoundingBoxObject(
    snapshotId: string,
    isPersistant = false,
    origin: BoundingBoxOrigin = "tool",
    visual: MeasureVisual = "box",
  ): void {
    if (!snapshotId) return;
    const existing = this.m_targets.find((t) => t.snapshotId === snapshotId);
    if (existing) {
      if (!isPersistant) return; // hover over an already-boxed target: no-op
      if (existing.isPersistent) {
        // Pin-click on the pinned target: Unity destroys ALL boxes.
        this.clearAll();
        return;
      }
      // Promote to pinned; pinning excludes every other box.
      this.m_targets = [{ snapshotId, isPersistent: true, origin, visual }];
      this.emit();
      return;
    }

    // New target: drop transients, keep pinned unless we're pinning.
    const kept = isPersistant ? [] : this.m_targets.filter((t) => t.isPersistent);
    this.m_targets = [
      ...kept,
      { snapshotId, isPersistent: isPersistant, origin, visual },
    ];
    this.emit();
  }

  setTargetVisual(snapshotId: string, visual: MeasureVisual): void {
    const existing = this.m_targets.find((t) => t.snapshotId === snapshotId);
    if (!existing || existing.visual === visual) return;
    this.m_targets = this.m_targets.map((t) =>
      t.snapshotId === snapshotId ? { ...t, visual } : t,
    );
    this.emit();
  }

  removeBoundingBoxObject(snapshotId?: string | null): void {
    if (!snapshotId) {
      this.clearAll();
      return;
    }
    const next = this.m_targets.filter(
      (t) => t.snapshotId !== snapshotId || t.isPersistent,
    );
    if (next.length === this.m_targets.length) return;
    this.m_targets = next;
    this.emit();
  }

  clearBoundingBoxes(origin: BoundingBoxOrigin): void {
    const next = this.m_targets.filter((t) => t.origin !== origin);
    if (next.length === this.m_targets.length) return;
    this.m_targets = next;
    this.emit();
  }

  private clearAll(): void {
    if (this.m_targets.length === 0) return;
    this.m_targets = [];
    this.emit();
  }

  private emit(): void {
    this.onChanged.emit(this.m_targets);
  }
}
